import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CopyWeekInput,
  CopyWeekResult,
  CreatePersonalActivityInput,
  CreateTimeEntryInput,
  DashboardPeriod,
  DashboardSummary,
  MonthResponse,
  OptionsResponse,
  PersonalActivity,
  PutCellInput,
  PutCellResponse,
  SessionInfo,
  TimeEntryList,
  TimeEntryMutationResponse,
  UpdateMeInput,
  UpdateTimeEntryInput,
  UserSelf,
  WeekResponse,
} from '@tt/shared';
import { api } from './api';

export const keys = {
  me: ['me'] as const,
  options: (date?: string, userId?: string) => ['options', date ?? null, userId ?? 'me'] as const,
  entries: (from: string, to: string, userId?: string) =>
    ['entries', from, to, userId ?? 'me'] as const,
  week: (date: string, keepRows: string[], userId?: string) =>
    ['week', date, keepRows.join(','), userId ?? 'me'] as const,
  month: (month: string, userId?: string) => ['month', month, userId ?? 'me'] as const,
  activities: ['personal-activities'] as const,
  dashboard: (period: DashboardPeriod, date?: string) =>
    ['dashboard', period, date ?? null] as const,
  sessions: ['sessions'] as const,
};

export const meQuery = queryOptions({
  queryKey: keys.me,
  queryFn: () => api<UserSelf>('/me'),
  staleTime: 5 * 60_000,
  retry: false,
});

export const useMe = () => useQuery(meQuery);

/** What may be booked to. `userId` asks on someone else's behalf, which only an admin may do. */
export const useOptions = (date?: string, opts: { enabled?: boolean; userId?: string } = {}) =>
  useQuery({
    queryKey: keys.options(date, opts.userId),
    queryFn: () => api<OptionsResponse>('/me/options', { query: { date, userId: opts.userId } }),
    staleTime: 60_000,
    enabled: opts.enabled ?? true,
  });

export const useEntries = (from: string, to: string, userId?: string) =>
  useQuery({
    queryKey: keys.entries(from, to, userId),
    queryFn: () => api<TimeEntryList>('/time-entries', { query: { from, to, userId } }),
  });

/** `userId` is only accepted for admins; omit it for the signed-in user's own timesheet. */
export const useWeek = (date: string, keepRows: string[], userId?: string) =>
  useQuery({
    queryKey: keys.week(date, keepRows, userId),
    queryFn: () =>
      api<WeekResponse>('/timesheet/week', {
        query: { date, userId, keepRows: keepRows.length ? keepRows.join(',') : undefined },
      }),
    placeholderData: (prev) => prev,
  });

export const useMonth = (month: string, userId?: string) =>
  useQuery({
    queryKey: keys.month(month, userId),
    queryFn: () => api<MonthResponse>('/timesheet/month', { query: { month, userId } }),
    placeholderData: (prev) => prev,
  });

export const usePersonalActivities = () =>
  useQuery({ queryKey: keys.activities, queryFn: () => api<PersonalActivity[]>('/me/activities') });

export const useDashboard = (period: DashboardPeriod, date?: string) =>
  useQuery({
    queryKey: keys.dashboard(period, date),
    queryFn: () => api<DashboardSummary>('/me/dashboard', { query: { period, date } }),
    placeholderData: (prev) => prev,
  });

export const useSessions = () =>
  useQuery({ queryKey: keys.sessions, queryFn: () => api<SessionInfo[]>('/me/sessions') });

/** Anything that changes entries invalidates every time-derived view. */
function useInvalidateTime() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['entries'] }),
      qc.invalidateQueries({ queryKey: ['week'] }),
      qc.invalidateQueries({ queryKey: ['month'] }),
      qc.invalidateQueries({ queryKey: ['dashboard'] }),
      qc.invalidateQueries({ queryKey: ['options'] }),
    ]);
}

export function useCreateEntry() {
  const invalidate = useInvalidateTime();
  return useMutation({
    mutationFn: (input: CreateTimeEntryInput) =>
      api<TimeEntryMutationResponse>('/time-entries', { method: 'POST', body: input }),
    onSuccess: invalidate,
  });
}

export function useUpdateEntry() {
  const invalidate = useInvalidateTime();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateTimeEntryInput & { id: string }) =>
      api<TimeEntryMutationResponse>(`/time-entries/${id}`, { method: 'PATCH', body: input }),
    onSuccess: invalidate,
  });
}

export function useDeleteEntry() {
  const invalidate = useInvalidateTime();
  return useMutation({
    mutationFn: (id: string) => api(`/time-entries/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function usePutCell() {
  const invalidate = useInvalidateTime();
  return useMutation({
    mutationFn: (input: PutCellInput) =>
      api<PutCellResponse>('/timesheet/cells', { method: 'PUT', body: input }),
    onSuccess: invalidate,
  });
}

export function useCreatePersonalActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePersonalActivityInput) =>
      api<PersonalActivity>('/me/activities', { method: 'POST', body: input }),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: keys.activities }),
        qc.invalidateQueries({ queryKey: ['options'] }),
      ]),
  });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMeInput) => api<UserSelf>('/me', { method: 'PATCH', body: input }),
    onSuccess: (data) => qc.setQueryData(keys.me, data),
  });
}

/**
 * Copies a week of entries onto another week (FR2 convenience action). `preview: true`
 * reports what would happen without writing, which the confirmation dialog shows first.
 */
export function useCopyWeek() {
  const invalidate = useInvalidateTime();
  return useMutation({
    mutationFn: (input: CopyWeekInput) =>
      api<CopyWeekResult>('/timesheet/copy-week', { method: 'POST', body: input }),
    onSuccess: (result) => (result.preview ? Promise.resolve() : invalidate()),
  });
}
